/**
 * API Endpoints
 * Centralized endpoint definitions
 */

export const API_ENDPOINTS = {
  // Authentication (Cognito: ME + SYNC; legacy: REGISTER, LOGIN, LOGOUT)
  AUTH: {
    REGISTER: '/auth/register',
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    ME: '/auth/me',
    SYNC: '/auth/sync',
    ACCEPT_TERMS: '/auth/accept-terms',
    PROFILE: '/auth/profile',
    DELETE_EXPIRED_TRIAL: '/auth/delete-expired-trial',
    ACTIVITY: '/auth/activity',
    CHANGE_PASSWORD: '/auth/change-password',
    SEND_SIGNUP_CODE: '/auth/send-signup-code',
    CONFIRM_SIGNUP: '/auth/confirm-signup',
    RESEND_SIGNUP_CODE: '/auth/resend-signup-code',
    COMPLETE_SIGNUP: '/auth/complete-signup',
  },

  // Subscription
  SUBSCRIPTION: {
    GET: '/subscription',
    UPDATE: '/subscription',
    CANCEL: '/subscription/cancel',
    STATUS: '/subscription/status',
  },

  // Documents
  DOCUMENTS: {
    CREATE: '/documents',
    LIST: '/documents',
    GET: (id: string) => `/documents/${id}`,
    UPDATE: (id: string) => `/documents/${id}`,
    DELETE: (id: string) => `/documents/${id}`,
    GENERATE_PDF: (id: string) => `/documents/${id}/generate-pdf`,
  },

  // Invoice history (paid tiers; no PDF stored, regenerate from payload)
  INVOICE_HISTORY: {
    LIST: '/invoice-history',
    COUNT: '/invoice-history/count',
    GET: (id: string) => `/invoice-history/${id}`,
    CREATE: '/invoice-history',
    UPDATE_PAYLOAD: (id: string) => `/invoice-history/${id}`,
    UPDATE_STATUS: (id: string) => `/invoice-history/${id}`,
    RECORD_EXTERNAL_PAYMENT: (id: string) => `/invoice-history/${id}/record-external-payment`,
    DELETE: (id: string) => `/invoice-history/${id}`,
    ANALYTICS: '/invoice-history/analytics',
    NEXT_NUMBER: '/invoice-history/next-number',
  },

  // Clients
  CLIENTS: {
    CREATE: '/clients',
    LIST: '/clients',
    GET: (id: string) => `/clients/${id}`,
    UPDATE: (id: string) => `/clients/${id}`,
    DELETE: (id: string) => `/clients/${id}`,
  },

  // Progress & Gamification (documentsGenerated etc.)
  PROGRESS: {
    GET: '/progress',
    INCREMENT: '/progress/increment',
  },

  ACHIEVEMENTS: {
    GET: '/achievements',
    UNLOCK: '/achievements/unlock',
  },

  // Settings
  SETTINGS: {
    GET: '/settings',
    UPDATE: '/settings',
  },

  // Email
  EMAIL: {
    SEND: '/email/send',
  },

  // SMS (SNS)
  SMS: {
    SEND: '/sms/send',
  },

  // Contact form (public; no auth)
  CONTACT: {
    SUBMIT: '/contact',
  },

  // Voice-to-invoice (AI Pro) – transcript or audio (record → upload → process; no Siri/Dictation)
  VOICE: {
    EXTRACT: '/voice/extract',
    UPLOAD_URL: '/voice/upload-url',
    PROCESS: '/voice/process',
    PROCESS_DIRECT: '/voice/process-direct',
  },

  // Payments (Stripe)
  PAYMENTS: {
    CHECKOUT_SESSION: '/payments/checkout-session',
    APPLY_COUPON: '/payments/apply-coupon',
    CREDITS_CHECKOUT: '/payments/credits-checkout',
    INVOICE_CHECKOUT: '/payments/invoice-checkout',
    CONNECT_STATUS: '/payments/connect/status',
    CONNECT_ONBOARDING: '/payments/connect/onboarding',
    CONNECT_DISCONNECT: '/payments/connect/disconnect',
    SESSION_SUCCESS: '/payments/session-success',
    CUSTOMER_PORTAL: '/payments/customer-portal',
    UPGRADE: '/payments/upgrade',
    DOWNGRADE: '/payments/downgrade',
    CANCEL_SCHEDULED_DOWNGRADE: '/payments/cancel-scheduled-downgrade',
    SWITCH_TO_FREE: '/payments/switch-to-free',
    RESUME_SUBSCRIPTION: '/payments/resume-subscription',
    CANCEL_ACCOUNT: '/payments/cancel-account',
  },

  // QuickBooks Online (push invoice, sync status for our invoices only)
  QUICKBOOKS: {
    CONNECT: '/quickbooks/connect',
    CALLBACK: '/quickbooks/callback',
    STATUS: '/quickbooks/status',
    DISCONNECT: '/quickbooks/disconnect',
    PUSH_INVOICE: '/quickbooks/push-invoice',
    PUSH_ALL: '/quickbooks/push-all',
    SYNC: '/quickbooks/sync',
    MARK_INVOICE_SENT: '/quickbooks/mark-invoice-sent',
  },
} as const;


